import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MessageCircle, Download, Loader2, FileText, Image as ImageIco, CheckCircle2, RefreshCw } from "lucide-react";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

function downloadFile(file) {
  const u = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = u;
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(u);
}

// Two-step WhatsApp share: the file is prepared first (may take seconds), then the actual share/open runs
// synchronously on a fresh tap — browsers reject navigator.share / window.open once the user-gesture window expires.
export default function ShareSheet({ open, onOpenChange, title, message, prepare, onShared, waHref }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setFile(null); setError(""); setBusy(true);
    try { setFile(await prepare()); } catch (e) { setError("Could not prepare the file. Please try again."); } finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(); else { setFile(null); setError(""); } }, [open]); // eslint-disable-line

  const canNative = !!(file && navigator.canShare && navigator.canShare({ files: [file] }));
  const isImage = file?.type?.startsWith("image/");

  const send = async () => {
    if (!file) return;
    try {
      if (canNative) {
        await navigator.share({ files: [file], text: message, title });
        toast.success("Shared to WhatsApp");
      } else {
        downloadFile(file);
        window.open(waHref || `https://wa.me/?text=${encodeURIComponent(message)}`, "_blank", "noopener");
        toast(`${isImage ? "Image" : "PDF"} downloaded — attach it in WhatsApp (a link cannot auto-attach a file).`, { duration: 7000 });
      }
      onShared && onShared();
      onOpenChange(false);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error(e?.name === "NotAllowedError" ? "Your browser blocked the share — tap Send again." : "Could not share");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-sheet">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B3B2E] flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" /> Share to WhatsApp</DialogTitle>
          <DialogDescription>{canNative ? "WhatsApp opens with the file attached and your message prefilled." : "The file downloads and WhatsApp opens with your message — attach the downloaded file in the chat."}</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-[#C9A34A]/30 bg-[#FAF7F0] px-4 py-3 flex items-center gap-3 min-h-[64px]" data-testid="share-file-state">
          {busy ? (
            <><Loader2 className="h-5 w-5 animate-spin text-[#14513D]" /><span className="text-sm text-[#14513D]">Preparing {title}…</span></>
          ) : error ? (
            <><span className="text-sm text-red-700 flex-1">{error}</span><Button size="sm" variant="outline" onClick={load} data-testid="share-retry" className="gap-1"><RefreshCw className="h-3.5 w-3.5" /> Retry</Button></>
          ) : file ? (
            <>
              {isImage ? <ImageIco className="h-5 w-5 text-[#C9A34A]" /> : <FileText className="h-5 w-5 text-[#C9A34A]" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#0B3B2E] truncate" data-testid="share-file-name">{file.name}</div>
                <div className="text-xs text-[#14513D]/70">{fmtSize(file.size)} · ready</div>
              </div>
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </>
          ) : null}
        </div>

        <div className="rounded-lg bg-[#0B3B2E]/5 px-3 py-2 text-xs text-[#14513D] italic" data-testid="share-message">“{message}”</div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={!file} onClick={() => file && downloadFile(file)} data-testid="share-download" className="gap-1"><Download className="h-4 w-4" /> Download only</Button>
          <Button disabled={!file} onClick={send} data-testid="share-send" className="gap-1 bg-[#25D366] hover:bg-[#1FB855] text-white">
            <MessageCircle className="h-4 w-4" /> {canNative ? "Send via WhatsApp" : "Open WhatsApp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
